/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useEffect, useState } from 'react';

// Lib dependencies
import {
	ActionIcon,
	Drawer,
	Flex,
	Group,
	Radio,
	Stack,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';

// Styles
import classes from './Filter.module.css';

// UI local components
import { Button, Title } from '@components/v2/ui';

// Icons
import { IconX } from '@tabler/icons-react';

// Utils
import { DatePickerInput } from '@components/v2/ui/DatePickerInput/DatePickerInput';
import { MultiSelect } from '@components/v2/ui/MultiSelect/MultiSelect';
import { ACLS } from '@config/constants/acls';
import { ROLES } from '@config/constants/roles';
import { useAuthorization } from '@lib/authorization';
import useStore from '@store/index';
import { checkAclFront } from '@utils/acl-front';
import {
	cloneDeep,
	convertArrayToLabelValue,
} from '@utils/functions';
import {
	MINT_STATUS_FILTER_OPTIONS,
	QUANTITY_CHECK_FILTER_OPTIONS,
} from '@utils/options';
import dayjs from 'dayjs';
import { useMeasurabilities } from 'shared/api/get-measurabilities';
import { useSites } from 'shared/api/get-sites';
import { useProduction } from '../../hooks/useProduction';

/* -------------------------------------------------------------------------- */
/*                                   Types                                     */
/* -------------------------------------------------------------------------- */

export type RangeFilter = {
	$gte: number;
	$lte: number;
};

export type QueryParams = {
	'location.countryCode'?: { $in: string[] };
	organization?: string[];
	project?: string[];
	site?: string[];
	quantityCheck?: string[];
	status?: string[];
	sellPrice_europertco2eq?: RangeFilter;
	'quantity.remaining_aco2'?: RangeFilter;
	[key: string]: unknown;
	startDate?: {
		$gte: string | Date;
		$lt: string | Date;
	};
};

type TFilterOffsetsProps = {
	opened: boolean;
	onClose: () => void;
	title: string;
	onApplyFilters: (filters: QueryParams) => void;
	value: QueryParams;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

export const Filter = ({
	opened,
	onClose,
	title,
	onApplyFilters,
	value,
}: TFilterOffsetsProps) => {
	/* ******************************** HOOKS ******************************* */

	const { t } = useTranslation();

	const {
		setProductionsQueryParams,
		setQueryParamsProjects,
	} = useProduction();

	const { setProductionsPage } = useStore();

	const [queryParams, setQueryParams] =
		useState<QueryParams>(value ?? {});

	const { data: projects } = useMeasurabilities();
	const { data: sites } = useSites({
		project: queryParams.project,
	});

	/* ******************************** HELPERS ******************************* */

	const handleReset = () => {
		setProductionsQueryParams({});
		setQueryParams({});
		onApplyFilters({});
		onClose();
	};

	const handleApplyFilters = () => {
		setProductionsQueryParams(queryParams);
		setProductionsPage(1);
		onApplyFilters(queryParams);
		onClose();
	};

	useEffect(() => {
		if (opened) {
			setQueryParams(cloneDeep(value ?? {}));
		}
	}, [opened, value]);

	const { role } = useAuthorization();

	/* ******************************** RENDERING ******************************* */
	return (
		<Drawer
			classNames={classes}
			opened={opened}
			position="right"
			onClose={onClose}
			withCloseButton={false}
			size="544px"
		>
			{/* Header */}
			<Flex
				align="center"
				gap="16px"
				justify="space-between"
				pb={'var(--mantine-spacing-xl)'}
			>
				<Title
					order={6}
					c="var(--mantine-color-igluPrimary-9)"
				>
					{title}
				</Title>
				<ActionIcon
					color="var(--mantine-color-igluBorder-9)"
					radius={'48px'}
					variant="outline"
					size={'48px'}
					onClick={onClose}
				>
					<IconX
						color="var(--mantine-color-igluPrimary-9)"
						size={'24px'}
					/>
				</ActionIcon>
			</Flex>

			{/* Drawer content */}
			<Flex
				flex={1}
				direction={'column'}
				justify="space-between"
			>
				<Stack gap={'var(--mantine-spacing-sm)'}>
					{checkAclFront(
						ACLS.FRONT.PRODUCTIONS.FILTER_DATE,
						role as ROLES
					) && (
						<DatePickerInput
							value={[
								queryParams.startDate?.$gte ?? '',
								queryParams.startDate?.$lt ?? '',
							]}
							onChange={(value) => {
								if (
									value.length === 2 &&
									value[0] === null &&
									value[1] === null
								) {
									const _cloneQueryParams =
										cloneDeep(queryParams);
									delete _cloneQueryParams['startDate'];
									setQueryParams(_cloneQueryParams);
									return;
								}
								setQueryParams({
									...queryParams,
									startDate: {
										$gte: value[0] ?? '',
										$lt: value[1] ?? '',
									},
								});
							}}
							size="md"
							placeholder="Filtrer par date"
							clearable
							type="range"
							presets={[
								{
									value: [
										dayjs()
											.startOf('week')
											.format('YYYY-MM-DD'),
										dayjs()
											.endOf('week')
											.format('YYYY-MM-DD'),
									],
									label: 'This week',
								},
								{
									value: [
										dayjs()
											.startOf('month')
											.format('YYYY-MM-DD'),
										dayjs()
											.endOf('month')
											.format('YYYY-MM-DD'),
									],
									label: 'This month',
								},
								{
									value: [
										dayjs()
											.startOf('year')
											.format('YYYY-MM-DD'),
										dayjs()
											.endOf('year')
											.format('YYYY-MM-DD'),
									],
									label: 'This year',
								},
							]}
						/>
					)}

					{checkAclFront(
						ACLS.FRONT.PRODUCTIONS.FILTER_VERIFIED_STATUS,
						role as ROLES
					) && (
						<Radio.Group
							label={t('global.verifiedStatus')}
							value={
								queryParams?.verified === true
									? 'verified'
									: queryParams?.verified === false
										? 'unverified'
										: 'all'
							}
							onChange={(value) => {
								if (value === 'all') {
									const { verified, ...rest } =
										queryParams;
									setQueryParams(rest);
								} else {
									setQueryParams({
										...queryParams,
										verified: value === 'verified',
									});
								}
							}}
						>
							<Group mt="xs">
								<Radio
									value="all"
									label={t('global.all')}
								/>
								<Radio
									value="verified"
									label={t('global.verified')}
								/>
								<Radio
									value="unverified"
									label={t('global.unverified')}
								/>
							</Group>
						</Radio.Group>
					)}

					{checkAclFront(
						ACLS.FRONT.PRODUCTIONS.FILTER_PROJECTS,
						role as ROLES
					) && (
						<MultiSelect
							placeholder={t('global.projects')}
							w={'100%'}
							comboboxProps={{ withinPortal: false }}
							data={convertArrayToLabelValue(projects)}
							clearable
							size="lg"
							value={queryParams.project ?? []}
							searchable
							onChange={(value) => {
								const queryParamsProjects = {
									project: value,
								};

								setQueryParamsProjects(
									queryParamsProjects
								);

								if (value.length === 0) {
									const _cloneQueryParams =
										cloneDeep(queryParams);
									delete _cloneQueryParams['project'];
									setQueryParams({
										..._cloneQueryParams,
									});
									return;
								}

								setQueryParams({
									...queryParams,
									project: value,
								});
							}}
						/>
					)}

					{checkAclFront(
						ACLS.FRONT.PRODUCTIONS.FILTER_SITES,
						role as ROLES
					) && (
						<MultiSelect
							placeholder={t('global.sites')}
							w={'100%'}
							comboboxProps={{ withinPortal: false }}
							data={convertArrayToLabelValue(sites)}
							clearable
							size="lg"
							value={queryParams.site ?? []}
							searchable
							onChange={(value) => {
								if (value.length === 0) {
									const _cloneQueryParams =
										cloneDeep(queryParams);
									delete _cloneQueryParams['site'];
									setQueryParams(_cloneQueryParams);
									return;
								}
								setQueryParams({
									...queryParams,
									site: value,
								});
							}}
						/>
					)}

					{checkAclFront(
						ACLS.FRONT.PRODUCTIONS.FILTER_QUANTITY_ALERT,
						role as ROLES
					) && (
						<MultiSelect
							placeholder={t('global.quantityCheck')}
							w={'100%'}
							comboboxProps={{ withinPortal: false }}
							data={QUANTITY_CHECK_FILTER_OPTIONS}
							clearable
							size="lg"
							value={queryParams.quantityCheck ?? []}
							searchable
							onChange={(value) => {
								if (value.length === 0) {
									const _cloneQueryParams =
										cloneDeep(queryParams);
									delete _cloneQueryParams[
										'quantityCheck'
									];
									setQueryParams(_cloneQueryParams);
									return;
								}
								setQueryParams({
									...queryParams,
									quantityCheck: value,
								});
							}}
						/>
					)}

					{checkAclFront(
						ACLS.FRONT.PRODUCTIONS.FILTER_MINTED_STATUS,
						role as ROLES
					) && (
						<MultiSelect
							placeholder={t(
								'filters.placeholders.selectMintStatus'
							)}
							w={'100%'}
							comboboxProps={{ withinPortal: false }}
							data={MINT_STATUS_FILTER_OPTIONS}
							clearable
							size="lg"
							value={queryParams.status ?? []}
							searchable
							onChange={(value) => {
								if (value.length === 0) {
									const _cloneQueryParams =
										cloneDeep(queryParams);
									delete _cloneQueryParams['status'];
									setQueryParams(_cloneQueryParams);
									return;
								}
								setQueryParams({
									...queryParams,
									status: value,
								});
							}}
						/>
					)}
				</Stack>

				{/* Buttons */}
				<Flex
					direction={{
						base: 'column-reverse',
						sm: 'row',
					}}
					gap={{
						base: 'var(--mantine-spacing-sm)',
						sm: 'var(--mantine-spacing-lg)',
					}}
				>
					<Button
						fullWidth
						variant={'outline'}
						onClick={handleReset}
					>
						{t('global.resetFilters')}
					</Button>
					<Button
						onClick={handleApplyFilters}
						fullWidth
						variant={'primary'}
					>
						{t('global.applyFilters')}
					</Button>
				</Flex>
			</Flex>
		</Drawer>
	);
};
